import { createRouter, createWebHistory } from 'vue-router'

import store from '@/store'
import { Types as authTypes } from '@/store/modules/authTypes'
import { getAccessToken } from '@/helper/auth-helper'

import nextFactory from './middleware/nextFactory'
import isNotAuth from './middleware/isNotAuth'
import auth from './middleware/auth'
import notFound from './middleware/notFound'

const routes = [{
    path: '/app/login', name: 'Login', meta: { layout: 'empty', middleware: isNotAuth },
    component: () => import('@/views/auth/Login'),
}, {
    path: '/app/reset-password', name: 'EmailForResetPassword', meta: { layout: 'empty', middleware: isNotAuth },
    component: () => import('@/views/auth/EmailForResetPassword'),
}, {
    path: '/app/reset-password/:token', name: 'ResetPassword', meta: { layout: 'empty', middleware: isNotAuth },
    component: () => import('@/views/auth/ResetPassword'),
}, {
    path: '/app/dashboard', name: 'Dashboard', meta: { layout: 'main', middleware: auth },
    component: () => import('@/views/Dashboard'),
}, {
    path: '/app/templates', name: 'Templates', redirect: { name: 'ManageTemplates' },
    component: () => import('@/views/templates/PageTemplates'),
    children: [{
        path: 'manage', name: 'ManageTemplates', meta: { layout: 'main', middleware: auth },
        component: () => import('@/views/templates/ManageTemplates'),
    }, {
        path: ':id/file-manager', name: 'FileManager', meta: { layout: 'main', middleware: auth },
        component: () => import('@/views/templates/FileManager'),
    }],
}, {
    path: '/app/campaigns', name: 'Campaigns', redirect: { name: 'ManageCampaigns' },
    component: () => import('@/views/campaigns/PageCampaigns'),
    children: [{
        path: 'manage', name: 'ManageCampaigns', meta: { layout: 'main', middleware: auth },
        component: () => import('@/views/campaigns/ManageCampaigns'),
    }, {
        path: 'create', name: 'CreateCampaign', meta: { layout: 'main', middleware: auth },
        component: () => import('@/views/campaigns/DetailCampaign'),
    }, {
        path: 'detail/:id', name: 'DetailCampaign', meta: { layout: 'main', middleware: auth },
        component: () => import('@/views/campaigns/DetailCampaign'),
    }, {
        path: 'cloud-front/:id', name: 'CloudFrontCampaign', meta: { layout: 'main', middleware: auth },
        component: () => import('@/views/campaigns/CloudFrontCampaign'),
    }, {
        path: 'error', name: 'ErrorCampaign', meta: { layout: 'main', middleware: auth },
        component: () => import('@/views/campaigns/ErrorLogCampaign'),
    }],
 }, {
    path: '/app/domains', name: 'Domains', redirect: { name: 'ManageDomains' },
    component: () => import('@/views/domains/PageDomains'),
    children: [{
        path: 'manage', name: 'ManageDomains', meta: { layout: 'main', middleware: auth },
        component: () => import('@/views/domains/ManageDomains'),
    }, {
        path: 'buy', name: 'BuyDomains', meta: { layout: 'main', middleware: auth },
        component: () => import('@/views/domains/BuyDomains'),
    }, {
        path: 'accounts', name: 'ManageDomainAccounts', meta: { layout: 'main', middleware: auth },
        component: () => import('@/views/domains/ManageDomainAccounts'),
    }],
}, {
    path: '/app/queues', name: 'Queues', redirect: { name: 'ManageQueues' },
    component: () => import('@/views/queue/PageQueues'),
    children: [{
        path: 'manage', name: 'ManageQueues', meta: { layout: 'main', middleware: auth },
        component: () => import('@/views/queue/ManageQueues'),
    }, {
        path: 'create', name: 'CreateQueue', meta: { layout: 'main', middleware: auth },
        component: () => import('@/views/queue/DetailQueue'),
    }, {
        path: 'detail/:id', name: 'DetailQueue', meta: { layout: 'main', middleware: auth },
        component: () => import('@/views/queue/DetailQueue'),
    }, {
        path: 'accounts', name: 'ManageIntegrationMethodAccounts', meta: { layout: 'main', middleware: auth },
        component: () => import('@/views/queue/ManageIntegrationMethodAccounts'),
    }],
}, {
    path: '/app/cloudfront', name: 'CloudFront', redirect: { name: 'ManageCDN' },
    component: () => import('@/views/cdn/PageCDN'),
    children: [{
        path: 'manage', name: 'ManageCDN', meta: { layout: 'main', middleware: auth },
        component: () => import('@/views/cdn/ManageCDN'),
    }, {
        path: 'create', name: 'CreateCDN', meta: { layout: 'main', middleware: auth },
        component: () => import('@/views/cdn/CreateCDN'),
    }, {
        path: 'accounts', name: 'ManageCDNAccounts', meta: { layout: 'main', middleware: auth },
        component: () => import('@/views/cdn/ManageCDNAccounts'),
    }],
}, {
    path: '/app/cloud-servers', name: 'Servers', redirect: { name: 'ManageCloudServers' },
    component: () => import('@/views/cloud-servers/PageCloudServers'),
    children: [{
        path: 'manage', name: 'ManageCloudServers', meta: { layout: 'main', middleware: auth },
        component: () => import('@/views/cloud-servers/ManageCloudServers'),
    }, {
        path: 'create', name: 'CreateCloudServers', meta: { layout: 'main', middleware: auth },
        component: () => import('@/views/cloud-servers/DetailCloudServers'),
    }, {
        path: 'domains', name: 'DomainsCloudServers', meta: { layout: 'main', middleware: auth },
        component: () => import('@/views/cloud-servers/DomainsCloudServers'),
    }, {
        path: 'accounts', name: 'ManageCloudServersAccounts', meta: { layout: 'main', middleware: auth },
        component: () => import('@/views/cloud-servers/ManageCloudServersAccounts'),
    }],
}, {
    path: '/app/users', name: 'Users', redirect: { name: 'ManageUsersRoles' },
    component: () => import('@/views/users/PageUsers'),
    children: [{
        path: 'manage-users', name: 'ManageUsers', meta: { layout: 'main', middleware: auth },
        component: () => import('@/views/users/ManageUsers')
    }, {
        path: 'roles/manage', name: 'ManageUsersRoles', meta: { layout: 'main', middleware: auth },
        component: () => import('@/views/users/ManageUsersRoles'),
    }, {
        path: 'roles/create', name: 'CreateRole', meta: { layout: 'main', middleware: auth },
        component: () => import('@/views/users/DetailRole'),
    }, {
        path: 'roles/:id', name: 'DetailRole', meta: { layout: 'main', middleware: auth },
        component: () => import('@/views/users/DetailRole'),
    }],
}, {
    path: '/app/cloudflare', name: 'Cloudflare', redirect: { name: 'ManageCloudflareDomains' },
    component: () => import('@/views/cloudflare/PageCloudflare'),
    children: [{
        path: 'manage-domains', name: 'ManageCloudflareDomains', meta: { layout: 'main', middleware: auth },
        component: () => import('@/views/cloudflare/ManageCloudflareDomains'),
    }, {
        path: 'manage-records/:accountId/:zoneId', name: 'ManageCloudflareRecords', meta: { layout: 'main', middleware: auth },
        component: () => import('@/views/cloudflare/ManageCloudflareRecords'),
    }, {
        path: 'add-domains', name: 'AddCloudflareZones', meta: { layout: 'main', middleware: auth },
        component: () => import('@/views/cloudflare/AddCloudflareZones'),
    }, {
        path: 'manage-accounts', name: 'ManageCloudflareAccounts', meta: { layout: 'main', middleware: auth },
        component: () => import('@/views/cloudflare/ManageCloudflareAccounts'),
    }],
}, {
    path: '/app/settings', name: 'Settings', meta: { layout: 'main', middleware: auth },
    component: () => import('@/views/settings/PageSettings'),
}, {
    path: '/not-found', alias: '/:pathMatch(.*)*', name: 'NotFound', meta: { layout: 'empty', middleware: notFound },
    component: () => import('@/views/NotFound'),
}]



const router = createRouter({
    history: createWebHistory(),
    routes,
})

router.beforeEach(async (to, from, next) => {
    if (!!getAccessToken() && !store.state.auth.accountId) {
        store.commit(authTypes.mutations.RESET_STATE)
        await store.dispatch(authTypes.actions.GET_CHECK)
    }

    if (to.meta.middleware) {
        const middleware = Array.isArray(to.meta.middleware) ? to.meta.middleware : [to.meta.middleware]
        const context = { to, from, next, store, router }
        const nextMiddleware = nextFactory(context, middleware, 1)

        return middleware[0]({ ...context, next: nextMiddleware })
    }

    return next()
})

export default router
